/**
 * Block 8: Unused Organizers Chart (Right Chart)
 * 
 * Table-style chart showing organizers with unused reservations
 * Styled to match RoomReservationRateChart (部屋別予約率)
 * Data source: meeting_room_mart_unused_organizers
 */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { QUERY_STRING_STORE } from '@/helpers/constants';
import { Paginator } from 'primereact/paginator';
import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import {
  fetchMeetingRoomMartUnusedOrganizers,
  getLastNMonths,
} from './query-request';
import './UnusedOrganizersChart.less';

// Month colors for stacked bars (oldest to newest) - matching design
// Pink -> Cyan -> Green -> Yellow (color wheel progression)
const MONTH_COLORS = ['#fac0bf', '#00b0f0', '#8ed973', '#f9c74f'];

@connect(state => ({
  querystring: state.querystring,
}))
class UnusedOrganizersChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      chartData: [],
      months: [],
      maxTotal: 0,
      sortField: 'total',
      sortDirection: 'down', // 'up', 'down', 'default' - matching RoomReservationRateChart
      first: 0,
      rows: 7,
      hoveredRow: null,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    const prevBranch = prevProps?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    const currentBranch = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    if (prevBranch !== currentBranch) {
      this.fetchData();
    }
  }

  fetchData = async () => {
    const tenantId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
    const branchId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];

    if (!tenantId) {
      this.setState({ chartData: [], maxTotal: 0 });
      return;
    }

    this.setState({ isLoading: true });

    try {
      const months = getLastNMonths(4);
      const data = await fetchMeetingRoomMartUnusedOrganizers({ tenantId, branchId, months });

      if (!data || data.length === 0) {
        this.setState({ chartData: [], maxTotal: 0, isLoading: false });
        return;
      }

      // Sort months from oldest to newest for stacking
      const sortedMonths = [...months].sort();

      // Group by organizer
      const organizerMap = new Map();
      data.forEach(d => {
        if (!organizerMap.has(d.organizer)) {
          organizerMap.set(d.organizer, {
            organizer: d.organizer,
            total: 0,
            monthData: {},
          });
        }
        const org = organizerMap.get(d.organizer);
        org.monthData[d.month] = {
          unused_minutes: d.unused_minutes || 0,
          rank: d.rank || 999,
        };
        org.total += d.unused_minutes || 0;
      });

      // Convert to array
      const chartData = Array.from(organizerMap.values());

      // Find max total for 100% reference
      const maxTotal = Math.max(...chartData.map(d => d.total), 1);

      // Sort by total descending (default)
      chartData.sort((a, b) => b.total - a.total);

      this.setState({
        chartData,
        months: sortedMonths,
        maxTotal,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching organizers data:', error);
      this.setState({ chartData: [], maxTotal: 0, isLoading: false });
    }
  };

  handleSort = (field) => {
    const { sortField, sortDirection } = this.state;

    let newDirection = 'down';
    if (sortField === field) {
      if (sortDirection === 'down') {
        newDirection = 'up';
      } else if (sortDirection === 'up') {
        newDirection = 'default';
      } else {
        newDirection = 'down';
      }
    }

    this.setState(prevState => {
      let sorted = [...prevState.chartData];

      if (newDirection !== 'default') {
        sorted.sort((a, b) => {
          const aVal = a[field];
          const bVal = b[field];
          const result = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal;
          return newDirection === 'up' ? result : -result;
        });
      }

      return {
        sortField: field,
        sortDirection: newDirection,
        chartData: sorted,
        first: 0,
      };
    });
  };

  handlePageChange = (e) => {
    this.setState({ first: e.first, rows: e.rows });
  };

  formatMonthLabel = (month) => {
    if (!month) return '';
    const [year, m] = month.split('-');
    return `${year}年${parseInt(m)}月`;
  };

  getSortIconSrc = (field) => {
    const { sortField, sortDirection } = this.state;
    if (sortField !== field) return '/assets/img/sort-icon.svg';
    if (sortDirection === 'up') return '/assets/img/sort-up-icon.svg';
    if (sortDirection === 'down') return '/assets/img/sort-down-icon.svg';
    return '/assets/img/sort-icon.svg';
  };

  renderProgressBar = (entry) => {
    const { months, maxTotal } = this.state;

    // Filter out months with 0 value to avoid rendering invisible segments
    const segments = months
      .map((month, monthIdx) => ({
        month,
        monthIdx,
        value: entry.monthData?.[month]?.unused_minutes || 0,
      }))
      .filter(seg => seg.value > 0);

    return (
      <div className="progress-bar-container">
        {segments.map((seg, idx) => {
          const width = (seg.value / maxTotal) * 100;
          const isFirst = idx === 0;
          const isLast = idx === segments.length - 1;
          const isOnly = segments.length === 1;

          let borderRadius = '0';
          if (isOnly) {
            borderRadius = '5px';
          } else if (isFirst) {
            borderRadius = '5px 0 0 5px';
          } else if (isLast) {
            borderRadius = '0 5px 5px 0';
          }

          return (
            <div
              key={seg.month}
              className="progress-bar-segment"
              style={{
                width: `${width}%`,
                backgroundColor: MONTH_COLORS[seg.monthIdx],
                borderRadius,
              }}
            />
          );
        })}
      </div>
    );
  };

  renderTooltip = (entry) => {
    const { months, hoveredRow } = this.state;

    if (hoveredRow !== entry.organizer) return null;

    return (
      <div className="custom-tooltip">
        <div className="tooltip-title">{entry.organizer}</div>
        {months.map((month) => {
          const monthData = entry.monthData?.[month];
          if (!monthData) return null;
          const rank = monthData.rank;
          const isTopTen = rank <= 10;
          return (
            <div key={month} className="tooltip-row">
              <span className="tooltip-label">{this.formatMonthLabel(month)}:</span>
              <span className="tooltip-value">
                {monthData.unused_minutes}分 (<span className={isTopTen ? 'rank-red' : ''}>{rank}位</span>)
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const { isLoading, chartData, first, rows } = this.state;
    const paginatedData = chartData.slice(first, first + rows);

    return (
      <div className="unused-organizers-chart">
        <div className="section-header">
          <h3 className="section-title">空予約時間</h3>
        </div>

        <div className="chart-content">
          {/* Table Header - matching RoomReservationRateChart style */}
          <div className="table-header">
            <div className="header-cell name-col">
              <span>予約者名</span>
              <button
                className="sort-button"
                onClick={() => this.handleSort('organizer')}
              >
                <img
                  className="custom-sort-icon"
                  src={this.getSortIconSrc('organizer')}
                  alt="sort"
                />
              </button>
            </div>
            <div className="header-cell value-col">
              <span>空予約時間</span>
              <button
                className="sort-button"
                onClick={() => this.handleSort('total')}
              >
                <img
                  className="custom-sort-icon"
                  src={this.getSortIconSrc('total')}
                  alt="sort"
                />
              </button>
            </div>
          </div>

          {/* Table Body */}
          <div className="table-body">
            {isLoading ? (
              <LoadingSpinner />
            ) : paginatedData.length === 0 ? (
              <div className="no-data-container">データがありません</div>
            ) : (
              paginatedData.map((entry) => (
                <div
                  key={entry.organizer}
                  className="table-row"
                  onMouseEnter={() => this.setState({ hoveredRow: entry.organizer })}
                  onMouseLeave={() => this.setState({ hoveredRow: null })}
                >
                  <div className="cell name-cell">
                    {entry.organizer}
                  </div>
                  <div className="cell value-cell">
                    <span className="value-text">{entry.total}</span>
                    {this.renderProgressBar(entry)}
                  </div>
                  {this.renderTooltip(entry)}
                </div>
              ))
            )}
          </div>

          {/* Pagination - always show */}
          <Paginator
            first={first}
            rows={rows}
            totalRecords={chartData.length}
            onPageChange={this.handlePageChange}
            className="custom-paginator"
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
          />
        </div>
      </div>
    );
  }
}

export default UnusedOrganizersChart;
